import React from 'react';
import { View, Text, Image, StyleSheet, TouchableOpacity, StyleProp, ViewStyle } from 'react-native';
import { COLORS } from '../theme/themeTokens';

interface AccountActionRowProps {
    onInflow?: () => void;
    onWithdraw?: () => void;
    onTopUp?: () => void;
    onExchange?: () => void;
    /** When true, render with the navy background variant used on screens
     *  that sit directly below SimpleHeader (e.g. BalanceDetailScreen). */
    onNavy?: boolean;
    /** Per-instance container override (e.g. to tweak paddingTop /
     *  paddingBottom). Stacked AFTER default styles so any property the
     *  override sets wins. */
    style?: StyleProp<ViewStyle>;
}

/**
 * Side-by-side action buttons rendered under the balance panel:
 * Inflow → Deposit, Withdraw → Withdraw, Top-up → DepositCard and, where a
 * handler is given, Exchange → Operations on the Exchange tab.
 *
 * Each action is a round colored button (icon only) with the action name
 * sitting below as a label. Buttons without a handler stay visible but
 * become non-interactive — keeps the row visually stable across screens
 * with different available actions.
 */
const AccountActionRow: React.FC<AccountActionRowProps> = ({
    onInflow,
    onWithdraw,
    onTopUp,
    onExchange,
    onNavy = false,
    style,
}) => {
    // Four columns need a tighter gap to fit a 375-pt screen; the
    // three-button row keeps the original spacing.
    const containerStyle = [
        styles.container,
        onExchange && styles.containerFour,
        onNavy && styles.containerOnNavy,
        style,
    ];

    return (
        <View style={containerStyle}>
            <View style={styles.actionItem}>
                <TouchableOpacity
                    style={[styles.button, styles.buttonInflow]}
                    onPress={onInflow}
                    disabled={!onInflow}
                    activeOpacity={0.75}
                >
                    <Image
                        source={require('../assets/arrowup.png')}
                        style={styles.icon}
                        resizeMode="contain"
                    />
                </TouchableOpacity>
                <Text style={styles.label}>Inflow</Text>
            </View>

            <View style={styles.actionItem}>
                <TouchableOpacity
                    style={[styles.button, styles.buttonWithdraw]}
                    onPress={onWithdraw}
                    disabled={!onWithdraw}
                    activeOpacity={0.75}
                >
                    <Image
                        source={require('../assets/withdraw.png')}
                        style={styles.icon}
                        resizeMode="contain"
                    />
                </TouchableOpacity>
                <Text style={styles.label}>Withdraw</Text>
            </View>

            <View style={styles.actionItem}>
                <TouchableOpacity
                    style={[styles.button, styles.buttonTopUp]}
                    onPress={onTopUp}
                    disabled={!onTopUp}
                    activeOpacity={0.75}
                >
                    <Image
                        source={require('../assets/top_up.png')}
                        style={styles.icon}
                        resizeMode="contain"
                    />
                </TouchableOpacity>
                <Text style={styles.label}>Top-up</Text>
            </View>

            {onExchange && (
                <View style={styles.actionItem}>
                    <TouchableOpacity
                        style={[styles.button, styles.buttonExchange]}
                        onPress={onExchange}
                        activeOpacity={0.75}
                        accessibilityRole="button"
                        accessibilityLabel="Exchange"
                    >
                        <Image
                            source={require('../assets/exchange_icon.png')}
                            style={styles.icon}
                            resizeMode="contain"
                        />
                    </TouchableOpacity>
                    <Text style={styles.label}>Exchange</Text>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        gap: 20,
        paddingHorizontal: 20,
        paddingVertical: 12,
    },
    containerFour: {
        gap: 12,
    },
    containerOnNavy: {
        backgroundColor: COLORS.primaryNavy,
        paddingTop: 4,
        // 0 — the parent owns the gap to whatever sits below.
        // BalanceDetailScreen uses networksFrame.paddingTop: 16 right
        // after this row, so the visible action-row → tile gap is 16 dp.
        // Was 20, which double-counted.
        paddingBottom: 0,
    },
    /* Each column: rectangular colored button on top, label underneath. */
    actionItem: {
        flex: 1,
        alignItems: 'center',
    },
    button: {
        alignSelf: 'stretch',
        height: 60,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 8,
    },
    buttonInflow: {
        backgroundColor: COLORS.brandOrange,
    },
    buttonWithdraw: {
        backgroundColor: COLORS.steelNavy,
    },
    buttonTopUp: {
        backgroundColor: COLORS.steelNavy,
    },
    buttonExchange: {
        backgroundColor: COLORS.steelNavy,
    },
    icon: {
        width: 28,
        height: 28,
        tintColor: COLORS.white,
    },
    label: {
        fontFamily: 'Inter',
        fontSize: 12,
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 14,
        color: COLORS.white,
    },
});

export default AccountActionRow;
